import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatIcon} from "@angular/material/icon";
import {MatButton, MatIconButton} from "@angular/material/button";
import {FlexModule} from "@angular/flex-layout";
import {IndustryScopeType, SelectableCategory, SelectableIndustry} from "../industry.model";
import {IndustryItemComponent} from "../industry-item/industry-item.component";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatError, MatFormField, MatHint, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatCardModule} from "@angular/material/card";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {MatMenuModule} from "@angular/material/menu";
import {MatDivider} from "@angular/material/divider";
import {MatTooltip} from "@angular/material/tooltip";
import {ManageIndustriesService} from "../manage-industries.service";
import {SnackbarService} from "@ui-kit/framework/util/service/snackbar.service";

@Component({
  selector: 'app-industry-category-card',
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    MatButton,
    FlexModule,
    IndustryItemComponent,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSlideToggle,
    MatHint,
    MatCardModule,
    MatSuffix,
    MatIconButton,
    MatMenuModule,
    MatDivider,
    ReactiveFormsModule,
    MatError,
    MatTooltip
  ],
  templateUrl: './industry-category-card.component.html',
  styleUrl: './industry-category-card.component.scss'
})
export class IndustryCategoryCardComponent implements OnInit {
  @Input() category!: SelectableCategory;
  @Output() categoryChanged = new EventEmitter<SelectableCategory>();
  @Output() addIndustryToCategory = new EventEmitter<number>();

  constructor(public loader: LoadingService,
              private fb: FormBuilder,
              private industriesService: ManageIndustriesService,
              private snackBar: SnackbarService
  ) {
  }

  isExpanded = true;
  isEditing = false;
  editForm!: FormGroup;

  // Pagination properties
  itemsPerPage = 5;
  currentPage = 1;
  showAll = false;

  ngOnInit() {
    this.initForm();
  }

  private initForm() {
    this.editForm = this.fb.group({
      labelName: ['', [Validators.maxLength(100)]],
      labelDescription: ['', [Validators.maxLength(500)]]
    });
  }

  get selectedCount(): number {
    return this.category.industries.filter(ind => ind.selected).length;
  }

  get totalCount(): number {
    return this.category.industries.length;
  }

  get displayName(): string {
    // For master categories, show override if exists, otherwise original name
    if (this.isMasterCategory) {
      return this.category.labelNameOverride || this.category.name;
    }
    // For offering categories, always show override or original
    return this.category.labelNameOverride || this.category.name;
  }

  get displayDescription(): string {
    // For master categories, show override if exists, otherwise original description
    if (this.isMasterCategory) {
      return this.category.labelDescriptionOverride || this.category.description || '';
    }
    // For offering categories, always show override or original
    return this.category.labelDescriptionOverride || this.category.description || '';
  }

  get isMasterCategory(): boolean {
    return this.category.typeScope === IndustryScopeType.MASTER;
  }

  get categoryIcon(): string {
    if (this.isMasterCategory) {
      return this.category.icon || 'category';
    }
    return 'settings'; // Custom categories use settings icon
  }

  get paginatedIndustries(): SelectableIndustry[] {
    if (this.showAll) {
      return this.category.industries;
    }

    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return this.category.industries.slice(startIndex, startIndex + this.itemsPerPage);
  }

  get totalPages(): number {
    return Math.ceil(this.category.industries.length / this.itemsPerPage);
  }

  get showPagination(): boolean {
    return this.category.industries.length > this.itemsPerPage;
  }

  get canSave(): boolean {
    return this.editForm.valid && this.editForm.dirty;
  }

  get canCancel(): boolean {
    return this.isEditing;
  }

  trackByIndustryId(index: number, industry: SelectableIndustry): any {
    return industry.id || industry.code;
  }

  //Note - leaving this code in but disabled for now in case it needs to be added back based on user feedback
  // toggleExpanded() {
  //   this.isExpanded = !this.isExpanded;
  // }

  onCategorySelectionChange() {
    // // When category is deselected, deselect all industries - removed
    // if (!this.category.selected) {
    //   this.category.industries.forEach(industry => {
    //     industry.selected = false;
    //   });
    // }
    this.emitChange();
  }

  startEditing() {
    this.isEditing = true;

    if (this.isMasterCategory) {
      this.editForm.patchValue({
        labelName: this.category.labelNameOverride || this.category.name,
        labelDescription: this.category.labelDescriptionOverride || this.category.description
      });
    } else {
      this.editForm.patchValue({
        labelName: this.category.name,
        labelDescription: this.category.description
      });
    }

    this.editForm.markAsPristine();
    this.editForm.markAsUntouched();
  }

  saveEditing() {
    if (!this.editForm.valid) {
      return;
    }

    const formValue = this.editForm.value;
    const trimmedName = formValue.labelName.trim();
    const trimmedDescription = formValue.labelDescription.trim();

    if (this.isMasterCategory) {
      this.category.labelNameOverride = trimmedName || this.category.name;
      this.category.labelDescriptionOverride = trimmedDescription || this.category.description;
      this.isEditing = false;
      this.emitChange();
    } else {
      this.loader.show();

      const updatedCategory = {
        ...this.category,
        name: trimmedName || this.category.name,
        description: trimmedDescription || this.category.description
      };

      this.industriesService.saveOfferingCategory(updatedCategory).subscribe({
        next: (savedCategory) => {
          this.category.name = savedCategory.name;
          this.category.description = savedCategory.description;
          this.isEditing = false;
          this.loader.hide();
        },
        error: (error) => {
          console.error('Error saving category:', error);
          this.snackBar.openSnackBar("An error occurred. Please try again later.");
          this.loader.hide();
        }
      });
    }
  }

  cancelEditing() {
    this.isEditing = false;
    this.editForm.reset();
  }

  onIndustryChanged(updatedIndustry: SelectableIndustry) {
    const index = this.category.industries.findIndex(ind => ind.id === updatedIndustry.id);
    if (index !== -1) {
      this.category.industries[index] = updatedIndustry;
      this.emitChange();
    }
  }

  addNewIndustry() {
    this.addIndustryToCategory.emit(this.category.id);
  }

  showAllIndustries() {
    this.showAll = true;
    this.currentPage = 1;
  }

  collapseIndustries() {
    this.showAll = false;
    this.currentPage = 1;
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  selectAllIndustries() {
    this.category.industries.forEach(industry => {
      industry.selected = true;
    });
    this.emitChange();
  }

  selectNoneIndustries() {
    this.category.industries.forEach(industry => {
      industry.selected = false;
    });
    this.emitChange();
  }

  resetLabelNameToDefault() {
    if (this.category.name) {
      this.editForm.patchValue({labelName: this.category.name});
      this.editForm.markAsDirty();
    }
  }

  resetLabelDescriptionToDefault() {
    if (this.category.description) {
      this.editForm.patchValue({labelDescription: this.category.description});
      this.editForm.markAsDirty();
    }
  }

  private emitChange() {
    this.categoryChanged.emit({...this.category});
  }
}
